// Re-filing a document onto the record it actually belongs to.
//
// CONSUMER: internal-ui.
//
// Grant, 2026-08-24: "The document would then move to that record. But
// if it's on that record it should also be somehow some way connected
// to the person record and household record too."
//
// The second half of that isn't handled here, and deliberately isn't:
// a family already gathers documents from all its records, and a person
// now gathers them from the records they're named on. Both are worked
// out from links the OS already keeps, so they follow the move by
// themselves — nothing has to be kept in step by hand.
//
// WHAT THIS SCREEN OWES THE PERSON USING IT
//
// Moving a client's document is a structural change: someone looking
// for it tomorrow goes where it used to be. So this says, in a
// sentence, where it is going and where it will still show up — before
// it happens, not after. And it hands back an Undo, because the honest
// answer to "did I put that in the right place?" is often "no".

import { useEffect, useMemo, useState } from "react";

import { apiV11, ApiError, type DocumentMoveTarget } from "../api";
import { Button, ErrorBanner } from "../ui";

interface DocumentMoveDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  /** Which family's records to offer. Null when the document's own
   *  record has no household — the dialog then explains rather than
   *  showing an empty list. */
  householdId: string | null;
  /** Where it is now, so the current home isn't offered as a
   *  destination and reads as "already here" instead. */
  currentEntityType: string;
  currentEntityId: string;
  onMoved: (undo: () => Promise<void>) => void;
}

export function DocumentMoveDialog({
  open,
  onClose,
  documentId,
  documentName,
  householdId,
  currentEntityType,
  currentEntityId,
  onMoved,
}: DocumentMoveDialogProps) {
  const [targets, setTargets] = useState<DocumentMoveTarget[] | null>(null);
  const [chosen, setChosen] = useState<DocumentMoveTarget | null>(null);
  const [reason, setReason] = useState("");
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTargets(null);
      setChosen(null);
      setReason("");
      setError(null);
      return;
    }
    if (!householdId) {
      setTargets([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiV11.listDocumentMoveTargets(householdId);
        if (!cancelled) setTargets(res.targets);
      } catch (err) {
        if (!cancelled) {
          setTargets([]);
          setError(
            err instanceof ApiError ? err.message : "Couldn't load this family's records.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, householdId]);

  // Grouped for scanning — a family with twenty records is a list you
  // read by company, not by scrolling.
  const grouped = useMemo(() => {
    const out = new Map<string, DocumentMoveTarget[]>();
    for (const t of targets ?? []) {
      // The current home is not a destination. Offering it would
      // produce a refusal from the server for something the screen
      // already knows.
      if (t.entityType === currentEntityType && t.entityId === currentEntityId) continue;
      const list = out.get(t.group) ?? [];
      list.push(t);
      out.set(t.group, list);
    }
    return [...out.entries()];
  }, [targets, currentEntityType, currentEntityId]);

  async function commit() {
    if (!chosen) return;
    setMoving(true);
    setError(null);
    try {
      const res = await apiV11.moveDocument(
        documentId,
        { entity_type: chosen.entityType, entity_id: chosen.entityId },
        reason.trim() || undefined,
      );
      const back = res.moved_from;
      onMoved(async () => {
        await apiV11.moveDocument(documentId, back, "Undo of an earlier move");
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't move this document.");
    } finally {
      setMoving(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="esign-newenv-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="File this document under a record"
    >
      <div className="esign-newenv">
        <header className="esign-newenv-head">
          <h2>File this under…</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </header>

        <p className="esign-newenv-hint">
          Moving <strong>{documentName}</strong>.
        </p>

        {error ? <ErrorBanner message={error} /> : null}

        {targets === null ? (
          <p className="esign-newenv-hint">Loading this family’s records…</p>
        ) : !householdId ? (
          <p className="esign-newenv-hint">
            This document isn’t attached to a family, so there’s nothing to file it under yet.
            Open the record it belongs to and upload it there.
          </p>
        ) : grouped.length === 0 ? (
          <p className="esign-newenv-hint">
            This family has no other records to file it under yet.
          </p>
        ) : (
          <div className="esign-newenv-step">
            {grouped.map(([group, items]) => (
              <div key={group}>
                <h3>{group}</h3>
                <ul className="esign-newenv-results">
                  {items.map((t) => (
                    <li key={`${t.entityType}:${t.entityId}`}>
                      <button
                        type="button"
                        onClick={() => setChosen(t)}
                        aria-pressed={
                          chosen?.entityId === t.entityId &&
                          chosen?.entityType === t.entityType
                        }
                        style={
                          chosen?.entityId === t.entityId &&
                          chosen?.entityType === t.entityType
                            ? { borderColor: "var(--blue)" }
                            : undefined
                        }
                      >
                        <strong>{t.label}</strong>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {chosen ? (
          <>
            {/* Said before it happens, in a sentence. The whole risk of
                moving a document is that the next person looks where it
                used to be — so the screen states where it will still
                turn up, rather than leaving that to be discovered. */}
            <p className="esign-newenv-hint">
              It will move to <strong>{chosen.label}</strong>, and still show on this family’s
              page and on the pages of anyone named on that record.
            </p>
            <label className="esign-newenv-newperson">
              <span>Why (optional — recorded in the audit log)</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <div className="esign-newenv-actions">
              <Button disabled={moving} onClick={() => void commit()}>
                {moving ? "Moving…" : `File under ${chosen.label}`}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setChosen(null)}>
                Pick a different record
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
